import { useState } from 'react';
import type { Passbook } from '@/shared/types';
import { cn } from '@/shared/lib/cn';
import { useChannelQuotas } from '../model/use-channel-quotas';

import { RechargeDialog } from './recharge-dialog';
import {
  formatCount,
  quotaLimit,
  quotaSignal,
  remainingPercent,
  type AccountCapacity,
  type QuotaSignal,
} from '@/features/marketing-shared';

function meterColor(signal: QuotaSignal): string {
  if (signal === 'exhausted') return 'bg-destructive';
  if (signal === 'approaching') return 'bg-warning';
  return 'bg-primary';
}

function ringColor(signal: QuotaSignal): string {
  if (signal === 'exhausted') return 'hsl(var(--destructive))';
  if (signal === 'approaching') return 'hsl(var(--warning))';
  return 'hsl(var(--primary))';
}

function RemainingMeter({
  label,
  quota,
  collapsed,
  onOpen,
}: {
  label: string;
  quota: AccountCapacity;
  collapsed?: boolean;
  onOpen: () => void;
}) {
  const limit = quotaLimit(quota);
  const left = remainingPercent(quota);
  const signal = quotaSignal(quota);
  const title = `${formatCount(quota.available)} slots de ${label.toLowerCase()} restantes de ${formatCount(limit)}`;

  if (collapsed) {
    return (
      <button
        type="button"
        title={title}
        aria-label={title}
        className="relative grid size-10 place-items-center"
        onClick={onOpen}
      >
        <span
          className="absolute inset-0 rounded-full"
          style={{ background: `conic-gradient(${ringColor(signal)} ${left * 3.6}deg, hsl(var(--muted)) 0deg)` }}
        />
        <span className="absolute inset-[3px] rounded-full bg-background" />
        <span className="relative text-[10px] font-semibold tabular-nums text-foreground">{left}</span>
      </button>
    );
  }

  return (
    <div className="mb-3 last:mb-0" title={title}>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-xs font-medium">{label}</span>
        <span
          className={cn(
            'text-xs font-semibold tabular-nums',
            signal === 'exhausted' ? 'text-destructive' : signal === 'approaching' ? 'text-warning' : 'text-foreground',
          )}
        >
          {formatCount(quota.available)}
        </span>
      </div>
      <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-muted">
        <div className={cn('h-full rounded-full transition-[width]', meterColor(signal))} style={{ width: `${left}%` }} />
      </div>
      <p className="mt-1 text-[11px] text-muted-foreground">de {formatCount(limit)} slots</p>
    </div>
  );
}

export function SidebarQuota({
  passbook,
  passbookId,
  collapsed,
}: {
  passbook?: Passbook | null;
  passbookId?: string;
  collapsed?: boolean;
}) {
  const emailOn = passbook?.hasEmailMarketing === true;
  const smsOn = passbook?.hasSmsMarketing === true;
  const [open, setOpen] = useState(false);
  const [channel, setChannel] = useState<'email' | 'sms'>('email');
  const { quotas, live } = useChannelQuotas(emailOn || smsOn ? passbookId : undefined);

  if (!passbookId || (!emailOn && !smsOn) || !quotas) return null;

  const rows = [
    emailOn ? { channel: 'email' as const, label: 'E-mail', quota: quotas.email } : null,
    smsOn ? { channel: 'sms' as const, label: 'SMS', quota: quotas.sms } : null,
  ].filter((row) => row != null);

  const openRecharge = (next: 'email' | 'sms') => {
    setChannel(next);
    setOpen(true);
  };

  return (
    <div className={cn('mb-2', collapsed && 'flex flex-col items-center gap-2')}>
      {rows.map((row) => (
        <RemainingMeter
          key={row.channel}
          label={row.label}
          quota={row.quota}
          collapsed={collapsed}
          onOpen={() => openRecharge(row.channel)}
        />
      ))}
      {collapsed || live ? null : <p className="mb-2 text-[11px] text-muted-foreground">Cota local, sem resposta do servidor.</p>}
      {collapsed ? null : (
        <button
          type="button"
          className="text-xs font-medium text-primary underline-offset-4 hover:underline"
          onClick={() => openRecharge(emailOn ? 'email' : 'sms')}
        >
          Recarregar
        </button>
      )}
      <RechargeDialog open={open} onOpenChange={setOpen} passbookId={passbookId} channel={channel} />
    </div>
  );
}
